import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Home, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReactNode } from "react";
import { FavoriteButton } from "./FavoriteButton";
import { Footer } from "./Footer";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface PageLayoutProps {
  children: ReactNode;
  calculatorName?: string;
  calculatorPath?: string;
  category?: string;
  subcategory?: string;
}

const categoryRoutes: Record<string, string> = {
  physics: "/physics",
  chemistry: "/chemistry",
  math: "/math",
  finance: "/finance",
  general: "/health",
  tools: "/tools",
};

const categoryLabels: Record<string, string> = {
  physics: "Physics",
  chemistry: "Chemistry",
  math: "Math",
  finance: "Finance",
  general: "General",
  tools: "Tools",
};

const subcategoryRoutes: Record<string, string> = {
  "Loans": "/loans",
  "Tax": "/tax",
};

export function PageLayout({
  children,
  calculatorName,
  calculatorPath,
  category,
  subcategory
}: PageLayoutProps) {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container py-8 md:py-12">
        {calculatorName && calculatorPath && (
          <div className="max-w-5xl mx-auto mb-6 space-y-4">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/" className="flex items-center gap-1">
                    <Home className="h-3.5 w-3.5" />
                    Home
                  </BreadcrumbLink>
                </BreadcrumbItem>
                
                {category && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink href={categoryRoutes[category] || "/"}>
                        {categoryLabels[category] || category}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                )}
                
                {subcategory && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink href={subcategoryRoutes[subcategory] || categoryRoutes[category] || "/"}>
                        {subcategory}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </>
                )}
                
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{calculatorName}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="flex items-center gap-3 flex-wrap">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate(-1)} 
                className="flex items-center gap-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <FavoriteButton 
                calculatorName={calculatorName} 
                calculatorPath={calculatorPath} 
              />
            </div>
          </div>
        )}

        <main className="max-w-5xl mx-auto">
          {children}
        </main>
        
        <Footer />
      </div>
    </div>
  );
}
